/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Component, inject, OnInit } from '@angular/core';
import { combineLatest, concatMap, Observable } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { AuthState } from '../state/auth.state';
import { Actions, Store } from '@ngxs/store';
import { LoggerService } from '../../../modules/shared/services/logger.service';
import { Navigate } from '@ngxs/router-plugin';
import { AppConfigState } from '../../state/appConfig/app-config.state';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { filter, map, take } from 'rxjs/operators';
import { DisclaimerSeen } from '../state/auth.actions';
import { DashGisUtil } from '../../../modules/shared/dash-gis-util';

/**
 * Acts as a director for the login process.
 * Evaluates current application state and redirects based on
 *  - config value enableDisclaimer
 *  - has the user viewed the disclaimer this session?
 *  - config value enableRemember
 *  - config value autologin
 *  - is the user currently authenticated?
 */
@Component({
    template: ` <div class="auth-component login-page">
        <router-outlet></router-outlet>
    </div>`,
    styleUrls: ['authentication.component.scss'],
    imports: [RouterOutlet],
})
export class AuthenticationComponent implements OnInit {
    appConfigLoaded$: Observable<boolean> = inject(Store).select(AppConfigState.loaded);
    enableDisclaimer$: Observable<boolean> = inject(Store).select(AppConfigState.enableDisclaimer);
    needsDisclaimer$: Observable<boolean> = inject(Store).select(AuthState.needsDisclaimer);
    excludedRoutes$: Observable<string[]> = inject(Store).select(AuthState.excludedRoutes);

    constructor(
        private logger: LoggerService,
        private authService: AuthService,
        private router: Router,
        private route: ActivatedRoute,
        private actions$: Actions,
        private store: Store
    ) {}

    public ngOnInit(): void {
        // this.actions$.pipe(ofActionSuccessful(AppConfigLoaded)).subscribe(() => {
        //     this.authService.refreshToken();
        // });
        const fullPath = DashGisUtil.buildPath(this.route.snapshot);
        const shouldShowDisclaimer$ = combineLatest([
            this.enableDisclaimer$,
            this.needsDisclaimer$,
            this.excludedRoutes$,
        ]).pipe(map(([enabled, needed, excludedRoutes]) => enabled && needed && !excludedRoutes.includes(fullPath)));

        this.appConfigLoaded$
            .pipe(
                filter(Boolean),
                concatMap(() => shouldShowDisclaimer$),
                take(1)
            )
            .subscribe((shouldShowDisclaimer) => {
                if (shouldShowDisclaimer) {
                    this.store.dispatch(new Navigate(['/auth/disclaimer'], null, { queryParamsHandling: 'preserve' }));
                } else {
                    this.store.dispatch(new DisclaimerSeen());
                }
            });
    }
}
